import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { AvatarUpload, FormRow, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import type {
  Client,
  WorkspaceCompanyFragment,
} from "@src/__generated__/urql-graphql";
import clientPlaceholderImageSrc from "@src/assets/img/client.png";
import { fieldToInputProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { CompanySelect } from "@src/widgets/CompanySelect";
import { observer, useLocalObservable } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, type ReactNode, useEffect } from "react";
import type { DeepPick } from "ts-essentials";
import { FormClient } from "./FormClient";

const USER_PLACEHOLDER_IMAGE =
  process.env.NODE_ENV === "test" ? "" : clientPlaceholderImageSrc;

export const ModalClient: FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    client?: {
      id: Client["id"];
      name: Client["name"];
      workspaceCompany: WorkspaceCompanyFragment;
      image?: DeepPick<
        Client["image"],
        {
          urls: {
            thumbnail: never;
          };
        }
      >;
    };
    onSubmit(arg: {
      photo?: File | null;
      id: string;
      name: string;
      workspace_company_id: string;
    }): void;
    isLoading: boolean;
    defaultName?: string;
  }>
> = observer(function ModalClient({
  isOpen,
  title,
  onClose,
  client,
  onSubmit,
  isLoading,
  defaultName,
}) {
  const { workspaceStore } = useStore();
  const state = useLocalObservable(() => ({
    form: new FormClient(),
    async save() {
      const { hasError } = await form.form.validate();
      if (hasError) return;

      onSubmit({
        ...form.serialize(),
        id: client?.id!,
      });
      handleClose();
    },
  }));

  const { form } = state;

  const handleClose = () => {
    onClose();
    state.form.name.reset();
    state.form.photo.reset();
  };

  useEffect(() => {
    if (!client) return;
    form.name.onChange(client.name);
    form.workspace_company_id.onChange(client.workspaceCompany.id);
  }, [client]);

  useEffect(() => {
    if (!defaultName) return;
    form.name.onChange(defaultName);
  }, [defaultName]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" trapFocus={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form className="grid grid-cols-12 gap-12">
            <div className="col-span-3">
              <AvatarUpload
                roundedFull
                placeholderImage={USER_PLACEHOLDER_IMAGE}
                imageSrc={client?.image?.urls.thumbnail ?? ""}
                onChange={state.form.photo.onChange}
              />
            </div>

            <div className="col-span-9">
              <FormRow title={<Trans>Name</Trans>}>
                <TextField id="field-name" {...fieldToInputProps(form.name)} />
              </FormRow>
              <FormRow
                title={t`Legal entity`}
                display={
                  workspaceStore.hasMultipleCompanies(
                    form.workspace_company_id.value,
                  ) || form.workspace_company_id.hasError
                    ? "flex"
                    : "none"
                }
              >
                <CompanySelect field={form.workspace_company_id} />
              </FormRow>
            </div>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={handleClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isDisabled={form.form.hasError}
            isLoading={isLoading}
            onClick={state.save}
          >
            <Trans>Save</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
